import { MPOST } from 'common/helpers.ts';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import { Button } from 'ui/component/Button.tsx';
import { IArchive } from 'ui/component/Icons.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Input } from 'ui/control/Input.tsx';
import { getTranslatedError } from '#admin/helpers.ts';

type Props = {
  id: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type FormValues = {
  reason: string;
};

export const ArchiveDialog = ({ id, open, onClose, onSuccess }: Props) => {
  const { t } = useTranslation();
  const formContext = useForm<FormValues>({ defaultValues: { reason: '' } });
  const { handleSubmit } = formContext;

  const { trigger, isMutating, error } = useSWRMutation(`/alert/${id}/archive`, MPOST);

  const handleConfirm = async (data: FormValues) => {
    const result = await trigger({ id, ...data });
    void mutate((key: string[]) => Array.isArray(key) && key[0] === '/alert/count');
    void mutate([`/shipment/${result.shipment_id}`]);
    onClose();
    onSuccess?.();
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={<b>{t('dialogs.archive.title')}</b>}>
      {isMutating && <Spinner centered={true} overlay={true} />}

      <FormProvider {...formContext}>
        <form className="flex w-[400px] flex-col gap-4" onSubmit={handleSubmit(handleConfirm)}>
          <div className="flex grow items-center justify-center">
            <IArchive size={128} className="blue rounded-full bg-blue-100 p-2" />
          </div>
          <p>{t('dialogs.archive.confirmMessage')}</p>
          <Input name="reason" label={null} placeholder={t('dialogs.archive.reasonPlaceholder')} required={true} autoFocus={true} />
          <div className="flex justify-end gap-2">
            <Button type="button" className="blue-outlined" onClick={onClose}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" className="red">
              {t('actions.archive')}
            </Button>
          </div>

          {error && <span className="text-center text-red-500">{getTranslatedError(error, t) || t('dialogs.alert.error')}</span>}
        </form>
      </FormProvider>
    </Modal>
  );
};
